import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import Layout from "../components/layout/Layout";
import Login from "../pages/auth/Login";
import AcceptInvite from "../pages/auth/AcceptInvite";
import Dashboard from "../pages/dashboard/Dashboard";
import EventList from "../pages/events/EventList";
import EventDetails from "../pages/events/EventDetails";
import CreateEvent from "../pages/events/CreateEvent";
import UploadMedia from "../pages/gallery/UploadMedia";
import GalleryList from "../pages/gallery/GalleryList";
import GalleryDetail from "../pages/gallery/GalleryDetail";
import RoomList from "../pages/rooms/RoomList";
import RoomGalleries from "../pages/rooms/RoomGalleries";
import RunningUploads from "../pages/uploads/RunningUploads";
import AutoUpload from "../pages/auto-upload/AutoUpload";
import CompanionInstall from "../pages/companion/CompanionInstall";
import LinkedDevices from "../pages/linked-devices/LinkedDevices";
import AccountSettings from "../pages/settings/AccountSettings";
import Wallet from "../pages/wallet/Wallet";
import GalleryPaymentDetail from "../pages/wallet/GalleryPaymentDetail";
import CashoutHistory from "../pages/wallet/CashoutHistory";
import BankDetails from "../pages/wallet/BankDetails";
import DisputeList from "../pages/disputes/DisputeList";
import DisputeForm from "../pages/disputes/DisputeForm";
import AccessRequests from "../pages/access-requests/AccessRequests";
import EventHistory from "../pages/history/EventHistory";
import MediaHistory from "../pages/events/MediaHistory";
import RoomSettings from "../pages/rooms/RoomSettings";
import PendingRoomRequests from "../pages/rooms/PendingRoomRequests";
import NonPublicAccessRequests from "../pages/rooms/NonPublicAccessRequests";
import SocialLinksEditor from "../components/rooms/SocialLinksEditor";
import Locations from "../pages/locations/Locations";
import NotificationsPage from "../pages/notifications/NotificationsPage";
import ChatInboxPage from "../pages/chat/ChatInboxPage";
import ChatThreadPage from "../pages/chat/ChatThreadPage";
import TeamPage from "../pages/team/TeamPage";
import SubAccountsPage from "../pages/sub-accounts/SubAccountsPage";
import AccessDenied from "../components/common/AccessDenied";

/**
 * `requiredPermission` gates a whole page behind one of the 9 Sub-Account
 * permission blocks (spec ch.11.2) - has no effect for a regular Business
 * Account (subAccountPermissions is only populated for Sub-Accounts).
 * Read/navigation pages that span several operational blocks accept any
 * relevant permission; mutation routes retain their single-block gate.
 * Personal pages (notifications, password/profile, chat) stay available.
 */
const ProtectedRoute = ({ children, requiredPermission, requiredAnyPermission }) => {
  const { token, isSubAccount, subAccountPermissions } = useAuth();
  if (!token) return <Navigate to="/login" replace />;

  const requiredPermissions = requiredAnyPermission || (requiredPermission ? [requiredPermission] : []);
  const isDenied = isSubAccount
    && requiredPermissions.length > 0
    && !requiredPermissions.some((permission) => subAccountPermissions?.[permission]);
  return <Layout>{isDenied ? <AccessDenied /> : children}</Layout>;
};

const EVENT_READ_PERMISSIONS = [
  "CanManageEvents", "CanManageAccessQrCheckIn", "CanManageGallery",
  "CanManageSingleMedia", "CanManagePricing", "CanManageSupport",
];
const ROOM_READ_PERMISSIONS = ["CanManageRooms", "CanManageGallery", "CanManageSingleMedia", "CanManagePricing"];

const AppRoutes = () => (
  <Routes>
    <Route path="/login" element={<Login />} />
    <Route path="/accept-invite" element={<AcceptInvite />} />

    <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
    <Route path="/events" element={<ProtectedRoute requiredAnyPermission={EVENT_READ_PERMISSIONS}><EventList /></ProtectedRoute>} />
    <Route path="/events/create" element={<ProtectedRoute requiredPermission="CanManageEvents"><CreateEvent /></ProtectedRoute>} />
    <Route path="/events/:id/edit" element={<ProtectedRoute requiredPermission="CanManageEvents"><CreateEvent /></ProtectedRoute>} />
    <Route path="/events/:id" element={<ProtectedRoute requiredAnyPermission={EVENT_READ_PERMISSIONS}><EventDetails /></ProtectedRoute>} />
    <Route path="/events/:eventId/gallery/:galleryId" element={<ProtectedRoute requiredPermission="CanManageGallery"><GalleryDetail /></ProtectedRoute>} />
    <Route path="/rooms/access-requests" element={<ProtectedRoute requiredPermission="CanManageAccessQrCheckIn"><NonPublicAccessRequests /></ProtectedRoute>} />
    <Route path="/rooms" element={<ProtectedRoute requiredAnyPermission={ROOM_READ_PERMISSIONS}><RoomList /></ProtectedRoute>} />
    <Route path="/rooms/:roomId/galleries" element={<ProtectedRoute requiredAnyPermission={ROOM_READ_PERMISSIONS}><RoomGalleries /></ProtectedRoute>} />
    <Route path="/rooms/:roomId/gallery/:galleryId" element={<ProtectedRoute requiredAnyPermission={["CanManageRooms", "CanManageGallery", "CanManageSingleMedia"]}><GalleryDetail /></ProtectedRoute>} />
    <Route path="/rooms/:roomId/settings" element={<ProtectedRoute requiredPermission="CanManageRooms"><RoomSettings /></ProtectedRoute>} />
    <Route path="/rooms/:roomId/pending-approval" element={<ProtectedRoute requiredPermission="CanManageRooms"><PendingRoomRequests /></ProtectedRoute>} />
    <Route path="/rooms/social-links" element={<ProtectedRoute requiredPermission="CanManageRooms"><SocialLinksEditor /></ProtectedRoute>} />
    <Route path="/locations" element={<ProtectedRoute requiredPermission="CanManageAccessQrCheckIn"><Locations /></ProtectedRoute>} />
    <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
    <Route path="/chat" element={<ProtectedRoute><ChatInboxPage /></ProtectedRoute>} />
    <Route path="/chat/:eventId" element={<ProtectedRoute><ChatThreadPage /></ProtectedRoute>} />
    <Route path="/team" element={<ProtectedRoute requiredPermission="CanManageEvents"><TeamPage /></ProtectedRoute>} />
    <Route path="/sub-accounts" element={<ProtectedRoute requiredPermission="CanManageSubAccounts"><SubAccountsPage /></ProtectedRoute>} />
    <Route path="/analytics" element={<Navigate to="/dashboard" replace />} />
    <Route path="/linked-devices" element={<ProtectedRoute requiredPermission="CanManageAccessQrCheckIn"><LinkedDevices /></ProtectedRoute>} />
    <Route path="/settings" element={<ProtectedRoute><AccountSettings /></ProtectedRoute>} />
    <Route path="/wallet" element={<ProtectedRoute requiredPermission="CanManageWallet"><Wallet /></ProtectedRoute>} />
    <Route path="/wallet/gallery/:galleryId" element={<ProtectedRoute requiredPermission="CanManageWallet"><GalleryPaymentDetail /></ProtectedRoute>} />
    <Route path="/wallet/cashout-history" element={<ProtectedRoute requiredPermission="CanManageWallet"><CashoutHistory /></ProtectedRoute>} />
    <Route path="/wallet/bank-details" element={<ProtectedRoute requiredPermission="CanManageWallet"><BankDetails /></ProtectedRoute>} />
    <Route path="/disputes" element={<ProtectedRoute requiredPermission="CanManageSupport"><DisputeList /></ProtectedRoute>} />
    <Route path="/disputes/new" element={<ProtectedRoute requiredPermission="CanManageSupport"><DisputeForm /></ProtectedRoute>} />
    <Route path="/disputes/:claimId/respond" element={<ProtectedRoute requiredPermission="CanManageSupport"><DisputeForm /></ProtectedRoute>} />
    <Route path="/access-requests" element={<ProtectedRoute requiredPermission="CanManageAccessQrCheckIn"><AccessRequests /></ProtectedRoute>} />
    <Route path="/history" element={<ProtectedRoute requiredAnyPermission={["CanManageEvents", "CanManageGallery", "CanManageSingleMedia"]}><EventHistory /></ProtectedRoute>} />
    <Route path="/events/:id/media-history" element={<ProtectedRoute requiredPermission="CanManageGallery"><MediaHistory /></ProtectedRoute>} />
    <Route path="/uploads" element={<ProtectedRoute requiredPermission="CanManageGallery"><RunningUploads /></ProtectedRoute>} />
    <Route path="/auto-upload" element={<ProtectedRoute requiredPermission="CanManageGallery"><AutoUpload /></ProtectedRoute>} />
    <Route path="/companion" element={<ProtectedRoute><CompanionInstall /></ProtectedRoute>} />
    <Route path="/upload" element={<ProtectedRoute requiredPermission="CanManageGallery"><UploadMedia /></ProtectedRoute>} />
    <Route path="/gallery" element={<ProtectedRoute requiredPermission="CanManageGallery"><GalleryList /></ProtectedRoute>} />

    <Route path="/" element={<Navigate to="/dashboard" replace />} />
    <Route path="*" element={<Navigate to="/dashboard" replace />} />
  </Routes>
);

export default AppRoutes;
